import {
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  InputGroup,
  InputLeftElement,
  InputProps,
  InputRightElement,
  useDisclosure,
} from '@chakra-ui/react';
import { forwardRef, ReactNode } from 'react';
import { MdArrowDropDown } from 'react-icons/md';
import Modalize from './modalize';
import { IDataWheelPickerSelect } from './whellPicker';
import { useFormContext } from 'react-hook-form';

interface IOwnProps extends InputProps {
  iconLeft?: ReactNode;
  placeholderLabel: string;
  isRequired?: boolean;
  listItens?: IDataWheelPickerSelect[];
  selectedItem?: IDataWheelPickerSelect;
  onChangeItem?: (data: IDataWheelPickerSelect | undefined) => void;
  isExternalState?: boolean;
}

const InputSelectMobile = forwardRef<HTMLInputElement, IOwnProps>((props, ref) => {
  const {
    iconLeft,
    placeholderLabel,
    isRequired,
    listItens = [],
    name = '',
    onChangeItem,
    selectedItem,
    isExternalState,
    ...rest
  } = props;

  const disclosure = useDisclosure();
  const contextForm = useFormContext();

  const errorMessage = contextForm?.formState.errors?.[name]?.message?.toString();

  const value = contextForm?.watch(name) || '';

  const selected = selectedItem || listItens.find((item) => item.id === value);

  const isDisabled = listItens.length === 0 || rest.isDisabled;

  const onChangeWheelPickerSelect = (item: IDataWheelPickerSelect) => {
    if (isExternalState && onChangeItem) {
      onChangeItem(item);
    } else {
      contextForm.setValue(name, item.id);
      if (onChangeItem) {
        onChangeItem(item);
      }
    }
  };

  return (
    <>
      <Modalize data={listItens} {...disclosure} onSelectItem={onChangeWheelPickerSelect} selectedItem={selected} />

      <FormControl
        isInvalid={!!errorMessage}
        sx={{
          transition: 'padding-top 0.2s ease',
          '&:has(input:focus), &:has(input:not(:placeholder-shown))': {
            paddingTop: '0.8rem',
          },
        }}
        isRequired={isRequired}
        position="relative"
      >
        <InputGroup
          size="sm"
          onClick={!isDisabled ? () => disclosure.onOpen() : undefined}
          tabIndex={0}
          onKeyDown={(e) => {
            if ((e.key === 'Enter' || e.key === ' ') && !isDisabled) {
              disclosure.onOpen();
            }
          }}
        >
          {iconLeft && <InputLeftElement pointerEvents="none">{iconLeft}</InputLeftElement>}
          <Input
            {...rest}
            placeholder=" "
            size="sm"
            pointerEvents="none"
            ref={ref}
            isReadOnly
            variant="flushed"
            isRequired={false}
            className="peer"
            isDisabled={isDisabled}
            value={selected?.name || ''}
          />
          <FormLabel
            htmlFor={name}
            position="absolute"
            left="2rem"
            top="50%"
            transform="translateY(-50%)"
            color="gray.500"
            fontSize="sm"
            transition="all 0.2s ease"
            pointerEvents="none"
            css={{
              '.peer:not(:placeholder-shown) ~ &, .peer:focus ~ &': {
                fontSize: '0.6rem',
                left: '0rem',
                top: '-4px',
              },
              '.peer:focus:not(:placeholder-shown) ~ &': {
                color: '#009ac0',
              },
            }}
          >
            {placeholderLabel}
          </FormLabel>
          <InputRightElement color={isDisabled ? 'gray.300' : 'black'} children={<MdArrowDropDown size={25} />} />
        </InputGroup>
        {errorMessage && <FormErrorMessage fontSize="0.78rem">{errorMessage}</FormErrorMessage>}
      </FormControl>
    </>
  );
});

export default InputSelectMobile;
